import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import GameCard from '@/components/base/GameCard';
import ArticleCard from '@/components/base/ArticleCard';
import { games, getFeaturedGames, getHotGames } from '@/mocks/games';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState } from 'react';

const featuredGames = getFeaturedGames();
const hotGames = getHotGames();

const heroGame = games.find((g) => g.id === 'game-6')!;

const rankingGames = [...games].sort((a, b) => b.rating - a.rating).slice(0, 8);

export default function Home() {
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setSubscribeStatus('success');
      setSubscribeMessage('订阅成功！感谢您的关注。');
      form.reset();
      return;
    }
    setSubscribeStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9qqupknlsngrm4lm490', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: (formData.get('email') as string || '').trim() }).toString(),
      });
      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = {}; }
      if (res.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMessage('订阅成功！感谢您的关注，最新游戏资讯将第一时间送达。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.toLowerCase().includes('spam') || text.toLowerCase().includes('spam')) {
          setSubscribeStatus('error');
          setSubscribeMessage('提交失败，请稍后再试。');
        } else {
          setSubscribeStatus('error');
          setSubscribeMessage(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMessage('网络错误，请检查连接后重试。');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'H游戏精选',
    url: 'https://xinchengtongxun.cn',
    description: '探索最新h游戏资源，涵盖各类h游戏攻略、排行榜与下载指南。最全面的h游戏内容导航与评测平台。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://xinchengtongxun.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'H游戏精选',
    url: 'https://xinchengtongxun.cn',
    description: '最全面的h游戏内容导航与评测平台，提供h游戏推荐、攻略、下载指南和排行榜。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@hyouxijingxuan.com',
      contactType: 'customer service',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Epic%20gaming%20landscape%20with%20silhouetted%20adventurer%20standing%20on%20cliff%20overlooking%20vast%20fantasy%20world%2C%20dramatic%20sunset%20sky%20with%20warm%20orange%20and%20golden%20hues%2C%20atmospheric%20lighting%2C%20cinematic%20composition%2C%20concept%20art%20style%20with%20soft%20gradients&width=1800&height=900&seq=home-hero-01&orientation=landscape"
            alt="H游戏精选 - 发现你的下一款游戏"
            title="H游戏精选推荐 - 最新h游戏合集与攻略"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight text-balance">
                探索最新<span className="text-primary-400">h游戏</span>世界
              </h1>
              <p className="text-sm md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                涵盖各类h游戏攻略、排行榜与下载指南。无论新手还是资深玩家，这里为你提供最全面的h游戏内容导航与评测。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/rpg"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap text-sm"
                >
                  <i className="ri-compass-3-line"></i>
                  探索热门游戏
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 hover:bg-white/25 text-white font-medium rounded-lg transition-colors backdrop-blur-sm whitespace-nowrap text-sm"
                >
                  <i className="ri-information-line"></i>
                  了解更多
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background-50 to-transparent h-16 md:h-24"></div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-8 relative z-10 mb-12">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group flex items-center gap-2 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-full hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Games */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mb-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                精选推荐
              </h2>
              <p className="text-sm text-foreground-500">编辑团队精心挑选的优质h游戏</p>
            </div>
            <Link
              to="/category/rpg"
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
            >
              查看全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
            {featuredGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        </section>

        {/* Hot Rankings + Latest News */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mb-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Rankings */}
            <div className="lg:col-span-1">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-5">
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-fire-line"></i>
                  </span>
                  <h2 className="text-lg font-heading font-bold text-foreground-950">热门排行</h2>
                </div>
                <div className="space-y-3">
                  {rankingGames.map((game, index) => (
                    <Link
                      key={game.id}
                      to={`/category/${game.categorySlug}`}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                      data-product-shop=""
                    >
                      <span className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-bold shrink-0 ${
                        index === 0 ? 'bg-accent-500 text-background-50' :
                        index === 1 ? 'bg-accent-400 text-background-50' :
                        index === 2 ? 'bg-accent-300 text-background-50' :
                        'bg-secondary-100 text-secondary-600'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {game.title}
                        </p>
                        <p className="text-[11px] text-foreground-500">{game.category}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <i className="ri-star-fill text-primary-500 text-[10px]"></i>
                        <span className="text-xs font-semibold text-primary-600">{game.rating}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Latest Articles */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                    最新资讯
                  </h2>
                  <p className="text-sm text-foreground-500">最新h游戏攻略、评测与资讯</p>
                </div>
              </div>
              <div className="space-y-4">
                {articles.slice(0, 4).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* More Games */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mb-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                更多推荐
              </h2>
              <p className="text-sm text-foreground-500">发现更多精彩的h游戏作品</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
            {games.filter((g) => !g.featured).slice(0, 8).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="relative overflow-hidden mb-14 mx-4 md:mx-6">
          <div className="max-w-7xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden bg-background-950">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20warm%20geometric%20pattern%20with%20flowing%20curves%20and%20dots%2C%20dark%20background%20with%20amber%20and%20golden%20accents%2C%20modern%20tech%20style%2C%20subtle%20gradient%20texture%2C%20minimalist%20design&width=1600&height=400&seq=newsletter-bg-01&orientation=landscape"
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
              <div className="relative py-12 md:py-16 px-6 md:px-12 text-center">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-white mb-3">
                  订阅最新h游戏资讯
                </h2>
                <p className="text-sm text-white/70 mb-8 max-w-lg mx-auto">
                  第一时间获取h游戏推荐、攻略和新游上线通知，不错过任何精彩内容。
                </p>
                <form
                  onSubmit={handleSubscribe}
                  data-readdy-form=""
                  className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
                >
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="contact_alt_field" />
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="请输入您的邮箱地址"
                    className="flex-1 w-full sm:w-auto px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={subscribeStatus === 'loading'}
                    className="w-full sm:w-auto px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap text-sm"
                  >
                    {subscribeStatus === 'loading' ? '提交中...' : '立即订阅'}
                  </button>
                </form>
                {subscribeStatus !== 'idle' && (
                  <p className={`mt-3 text-xs ${subscribeStatus === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                    {subscribeMessage}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}